import {
  defineNuxtRouteMiddleware,
  useNuxtApp,
  useRuntimeConfig
} from "#imports";
import { useHttpFetch } from "@backmarket/nuxt-module-http-v2/useHttpFetch";
import { useLogger } from "@backmarket/nuxt-module-logger/useLogger";
import { tryCatch } from "@backmarket/utils/function/tryCatch";
import hashByRoute from "~~/.translations/hash-by-route.js";
import { useI18nLocale } from "../composables/useI18nLocale.js";
import { useIsTranslationsSplitV2Enabled } from "../composables/useIsTranslationsSplitV2Enabled.js";
import { useVisitedRoutes } from "../composables/useVisitedRoutes.js";
import { CLIENT_GLOBAL_KEY } from "../constants.js";
export default defineNuxtRouteMiddleware(async (to) => {
  if (import.meta.server) return;
  const logger = useLogger();
  let hash;
  try {
    const app = useNuxtApp();
    if (app.isHydrating) {
      return;
    }
    const config = useRuntimeConfig();
    const { appName } = config.public.i18n;
    const translationsSplitV2Enabled = useIsTranslationsSplitV2Enabled();
    if (!translationsSplitV2Enabled) {
      return;
    }
    const { hasBeenVisited, visitRoute } = useVisitedRoutes();
    const routeName = String(to.name);
    if (!routeName) {
      return;
    }
    hash = hashByRoute[routeName];
    if (!hash) {
      return;
    }
    if (hasBeenVisited(hash)) {
      return;
    }
    const locale = useI18nLocale();
    const $httpFetch = useHttpFetch();
    const routeTranslations = await retry(
      () => $httpFetch("/:locale/:app/translations/:hash", {
        pathParams: {
          locale,
          app: appName,
          hash
        },
        baseURL: window.location.origin,
        timeout: 5e3,
        // ofetch retries 1 by default on some status codes
        retry: 0
      }),
      { retries: 2 }
    );
    window[CLIENT_GLOBAL_KEY].translations = {
      ...window[CLIENT_GLOBAL_KEY].translations || {},
      ...routeTranslations || {}
    };
    visitRoute(hash);
  } catch (err) {
    logger.error(
      "[Translations Split][Middleware] Error loading route translations",
      {
        error: err,
        errorDetails: {
          hash,
          statusCode: err?.statusCode,
          name: err?.name
        },
        owners: [
          "bot-chapter-frontend-core-pastrami",
          "bot-squad-content-front",
          "bot-squad-code-foundations-front"
        ],
        to: String(to?.name || "unknown")
      }
    );
  }
});
async function retry(fn, { retries }) {
  const [result, error] = await tryCatch(fn);
  if (error && retries > 0) {
    return retry(fn, { retries: retries - 1 });
  }
  if (error) {
    throw error;
  }
  return result;
}
